var _ = require('underscore');
var $ = require('jquery');
var BrecTableView = require('./../widget/brec-table/BrecTableView');
var launcherAppInterface = require('./../launcherApplicationInterface');

var makePaymentWidgetTemplate = require('./makePaymentWidgetTemplate.hbs');

var applicationUtils = require('./../applicationUtils');
var UseModalBehavior = require('./../widget/modal/UseModalBehavior');
var RenderIfDisplayedBehavior = require('./../behaviors/RenderIfDisplayedBehavior');
var DemographicsDataBehavior = require('./../demographics/DemographicsDataBehavior');
var AppointmentTypeParentBehavior = require('./../behaviors/AppointmentTypeParentBehavior');
var AppointmentTypeDataBehavior = require('./../appointment-type/AppointmentTypeDataBehavior');
var SecureMessageDataBehavior = require('./../secure-message/SecureMessageDataBehavior');

var StoredPaymentMethodService = require('./../payment-method/StoredPaymentMethodService');
var PaymentDetailsFormModel = require('./PaymentDetailsFormModel');
var PaymentDetailsType = PaymentDetailsFormModel.PAYMENT_DETAILS_TYPE;
var PayableBillsListView = require('./PayableBillsListView');
var PayableEstimatesListView = require('./PayableEstimatesListView');
var SubmitPaymentModalView = require('./SubmitPaymentModalView');
var SelectPaymentMethodView = require('./SelectPaymentMethodView');

var financialFeatureConfigModel = require('./../financial/financialFeatureConfigModel');
var billingConfigModel = require('./../financial/billingConfigModel');
var handlebars = require('hbsfy/runtime');

var withSidebarClassNameModifier = '';
if (applicationUtils.currentApplication === applicationUtils.APPLICATIONS.ADMIN_TOOL) {
  withSidebarClassNameModifier = ' brec-tab__content--side';
}

/**
 * Convert a generic jqXHR into a JavaScript representation of com.vecna.rest.common.exceptions.ValidationException
 * @param  {jqXHR} jqXHR for an error response caused by a ValidationException or any other WebApplicationException
 * @return {Object} the ValidationException or a ValidationException with a generic error message
 */
function toValidationException(jqXHR) {
  var isValidationException = jqXHR.getResponseHeader('Content-Type') === 'application/json';

  if (isValidationException) {
    return JSON.parse(jqXHR.responseText);
  }
  return {generalErrors: [{errorKey: 'payment.serverErrors.status.' + jqXHR.status}]};
}

/**
 * Helper function to parse the error result from a FormModel save
 * @param  {Object} formModelResponses the reason for the rejected promise returned by a FormModel save
 * @return {Array} an array of ErrorMessageDTO Objects from all error categories across all models
 */
function parseServerErrors(formModelResponses) {
  return _.chain(formModelResponses)
    .where({success: false})
    .map(function (modelResponse) {
      if (modelResponse.response && modelResponse.response.length > 0) {
        var jqXHR = modelResponse.response[0];
        return toValidationException(jqXHR);
      }
      return {generalErrors: [{errorKey: 'payment.serverErrors.status.500'}]};
    })
    .reduce(function (allErrors, modelErrorsByCategory) {
      var modelErrors = _.flatten(_.values(modelErrorsByCategory), true);
      return allErrors.concat(modelErrors);
    }, [])
    .value();
}

/**
 * Widget for Make Payment
 *
 * @class MakePaymentWidget
 * @constructor
 * @param options {Object}
 *   @param options.patientVecnaId {String} the vecna id of the patient that the payment is being made for.
 *   @param options.appointmentExternalId {String} the external id of the appointment that the payment is associated with.
 */
module.exports = BrecTableView.extend({
  tagName: 'section',
  className: 'brec-tab__content' + withSidebarClassNameModifier,
  id: 'tab-content',
  template: makePaymentWidgetTemplate,

  /**
   * The view to handle bills associated with the current payment details.
   *
   * @property _billsView
   * @type PayableBillsListView
   * @private
   */
  _billsView: null,

  /**
   * The view to handle selecting the payment method to use for this payment.
   *
   * @property _selectPaymentMethodView
   * @type SelectPaymentMethodView
   * @private
   */
  _selectPaymentMethodView: null,

  /**
   * ID for the patient this payment is for.
   * @property _patientVecnaId
   * @type String
   * @private
   */
  _patientVecnaId: null,

  /**
   * External ID for the appointment that is being pre-registered for when this payment was started.
   * @property _appointmentExternalId
   * @type String
   * @private
   */
  _appointmentExternalId: null,

  prepareFields: {
    isInPreReg: '_isInPreReg',
    financialFeatureConfig:  financialFeatureConfigModel,
    totalAmountUnpaidDue: '_getTotalAmountUnpaidDue',
    patientVecnaId: function() {
      return this._patientVecnaId;
    }
  },

  injectionSites: {
    bills: '_billsView',
    estimates: '_estimatesView',
    'select-payment-method': {
      view: function() {
        // needed because it is shared and injection sites will steal the view if the parent view renders.
        if (this.isActive() && this.isAttached()) {
          return this._selectPaymentMethodView;
        }
      },
      options: { shared: true }
    }
  },

  behaviors: {
    useModalBehavior: UseModalBehavior,
    renderIfDisplayed: RenderIfDisplayedBehavior,
    demographics: {
      behavior: DemographicsDataBehavior,
      returnSingleResult: true,
      renderOnFetch: true,
      id: { property: '_patientVecnaId' }
    },
    appointmentTypeParentBehavior: {
      behavior: AppointmentTypeParentBehavior,
      renderOnParentFetched: false
    },
    appointmentSecureMessages: {
      behavior: SecureMessageDataBehavior,
      renderOnFetch: true,
      ids: function(cache) {
        var appointmentExternalIds = this.view._appointmentExternalIds;
        if (_.isEmpty(appointmentExternalIds)) {
          return { skipObjectRetrieval: true };
        }

        var patientVecnaId = this.view._patientVecnaId;
        var criteria = cache.getCriteriaForAppointmentIdAndExternalId(patientVecnaId, appointmentExternalIds);
        return cache.fetchIdsByCriteria(criteria);
      }
    },
    appointmentType: {
      behavior: AppointmentTypeDataBehavior,
      renderOnFetch: true,
      ids: { property: 'behaviors.appointmentSecureMessages.data:appointmentDTO.externalAppointment.type'}
    },
    parentAppointmentType: {
      behavior: AppointmentTypeDataBehavior,
      ids: {property: 'behaviors.appointmentType.data:parentExternalId'}
    },
    grandparentAppointmentType: {
      behavior: AppointmentTypeDataBehavior,
      ids: {property: 'behaviors.parentAppointmentType.data:parentExternalId'}
    }
  },

  events: {
    'click [data-submit-payment]': '_startProcessPayment',
    'click [data-pay-all-btn]' : '_payAll',
    'click [data-cancel-payment]': '_cancelPayment'
  },

  feedback: [
    /**
     * When someone blurs from the partial payment amount input field,
     * Then run getTotalPaymentAmount
     * @property feedback[0] Total of Payments Selected or Entered {When/Then Feedback}
     */
    {
      when: {
        listenTo: [
          {
            object: 'model',
            events: 'change:billPaymentDetails'
          }
        ]
      },
      then: function() {
        return {
          text: handlebars.helpers.formatCurrency(this.model.getTotalPaymentAmount())
        };
      },
      to: 'total-payment-amount'
    },
    /**
     * When billPaymentDetails has validation error,
     * Then disable the Submit Payment button
     * @property feedback[1] (Disable/Enable Submit Payment button) {When/Then Feedback}
     */
    {
      when: {
        listenTo: [
          {
            object: 'model',
            events: 'change'
          },
          {
            object: function() {
              return this.getBehavior('demographics');
            },
            events: 'change:fetchSuccess'
          }
        ]
      },
      then: function() {
        var errors = this.model.validate();
        errors = _.keys(errors);
        var noErrors = errors.length === 0;
        var cardNeedsEncryption = errors.length === 1 && _.contains(errors, 'cardData.encryptedCardInfo');

        var fetchDemographicsSuccess = this.getBehavior('demographics').get('fetchSuccess');
        var hasPatientVecnaId = this._patientVecnaId;

        if (( noErrors || cardNeedsEncryption ) && fetchDemographicsSuccess && hasPatientVecnaId) {
          return {
            removeClass: 'brec-btn--disabled',
            removeAttr: ['disabled', 'disabled']
          };
        } else {
          return {
            addClass: 'brec-btn--disabled',
            attr: ['disabled', 'disabled']
          };
        }
      },
      to: 'submit-payment'
    },
    /**
     * When billPaymentDetails has validation error,
     * Then show the warning feedback box and set "aria-hidden" to "true".
     * @property feedback[2] (Show validation warning feedback box) {When/Then Feedback}
     */
    {
      when: {
        listenTo: [
          {
            object: 'model',
            events: 'change'
          }
        ]
      },
      then: function() {
        var errors = this.model.validate();
        errors = _.keys(errors);
        var noErrors = errors.length === 0;
        var cardNeedsEncryption = errors.length === 1 && _.contains(errors, 'cardData.encryptedCardInfo');

        // Do not show warning message if the bills are still being fetched,
        // or if there are no payable bills (handled by a separate error message)
        var hasBalance = this._getTotalAmountUnpaidDue() > 0;

        // Do not show warning messages if the Submit Payment modal is open
        var submitPaymentModalOpen = this._submitPaymentModal && this._submitPaymentModal.isActive() && this._submitPaymentModal.isAttached();

        // Do not show warning messages if the Select Payment Method view is hidden on the Make Payment page
        var hideSelectPaymentMethod = this.get('hide:select-payment-method');

        if ( noErrors || cardNeedsEncryption || !hasBalance || submitPaymentModalOpen || hideSelectPaymentMethod) {
          return {
            addClass: 'brec-hidden',
            attr: ['aria-hidden', 'true']
          };
        } else {
          return {
            removeClass: 'brec-hidden',
            attr: ['aria-hidden', 'false']
          };
        }
      },
      to: 'submitPaymentValidationWarningBox'
    },
    /**
     * When billPaymentDetails has validation error,
     * Then display the first error as a warning message.
     * @property feedback[3] (Submit Payment validation warning) {When/Then Feedback}
     */
    {
      when: {
        listenTo: [
          {
            object: 'model',
            events: 'change'
          }
        ]
      },
      then: function() {
        var errors = this.model.validate();
        errors = _.keys(errors);
        var noErrors = errors.length === 0;
        var cardNeedsEncryption = errors.length === 1 && _.contains(errors, 'cardData.encryptedCardInfo');
        if ( noErrors || cardNeedsEncryption) {
          return {
            text: '',
            attr: ['data-test-encrypted-card-info', false]
          };
        } else if (errors[0] === 'cardData.encryptedCardInfo') {
          return {
            text: $.i18n.prop('payment.validationWarning.' + errors[1]),
            attr: ['data-test-payment-validation-warning', errors[1]]
          };
        } else {
          return {
            text: $.i18n.prop('payment.validationWarning.' + errors[0]),
            attr: ['data-test-payment-validation-warning', errors[0]]
          };
        }
      },
      to: 'submitPaymentValidationWarning'
    }
  ],

  /**
   * @method initialize
   * @param [options={}] {Object}
   *   @param [options.patientVecnaId=undefined] {String} the vecna id of the patient that the payment is being made for.
   *   @param [options.billId=undefined] {String} the serviceId of the selected bill.
   *   @param [options.accountExternalId] {Number} the id number of the selected estimate.
   *   @param [options.appointmentExternalId=undefined] {String} the external id of the appointment that the payment is associated with.
   *   @param [options.appointmentExternalIds=undefined] {String[]} the external ids of the appointments to pre-select copays for.
   *   @param [options.isInPreReg] {boolean} whether it's in pre-reg or not
   */
  initialize: function (options) {
    options = options || {};
    _.bindAll(this, '_completePayment', '_cancelPayment', '_paymentFailed');

    this._appointmentExternalIds = options.appointmentExternalIds;
    this._accountExternalId = options.accountExternalId;
    this._patientVecnaId = options.patientVecnaId;
    this._isInPreReg = options.isInPreReg;

    // Initialize the form model used by this view's child form views.
    var appointmentExternalId = null;
    if (!_.isEmpty(this._appointmentExternalIds)) {
      appointmentExternalId = this._appointmentExternalIds[0];
    }
    var paymentDetailsFormModel = new PaymentDetailsFormModel(null, {
      isMakingPayment: true,
      patientVecnaId: this._patientVecnaId,
      appointmentExternalId: appointmentExternalId
    });
    this.model = paymentDetailsFormModel;

    if (!launcherAppInterface.isAtHospital()) {
      // Setup Stored payment models.
      this._storedPaymentMethodService = new StoredPaymentMethodService({
        formModel: paymentDetailsFormModel,
        patientVecnaId: this._patientVecnaId
      });
    }

    // Instantiate the bill information child view.
    // A side effect of this is to set the bill message collection on the PaymentDetailsFormModel which is used by some
    // of its public methods.
    this._billsView = new PayableBillsListView({
      tableState: this.tableState,
      model: paymentDetailsFormModel,
      patientVecnaId: this._patientVecnaId,
      billId: options.billId
    });
    this._billMessageCollection = this._billsView.collection;
    this.listenTo(this._billMessageCollection, 'add remove reset change', this.render);


    // Add additional class to the widget in pre-reg.
    this._setAttributes({
      'class': this.className + ' portal-container portal-container--prereg-payment-widget'
    });

    // Instantiate the estimates information child view.
    this._estimatesView = new PayableEstimatesListView({
      tableState: this.tableState,
      model: paymentDetailsFormModel,
      patientVecnaId: this._patientVecnaId,
      appointmentExternalIds: this._appointmentExternalIds
    });
    this._insuranceEstimationCollection = this._estimatesView.collection;
    this.listenTo(this._insuranceEstimationCollection, 'add remove reset change', this.render);

    this._selectPaymentMethodView = new SelectPaymentMethodView({
      patientVecnaId: this._patientVecnaId,
      paymentDetailsFormModel: paymentDetailsFormModel,
      storedPaymentMethodService: this._storedPaymentMethodService
    });

    this.on('submit', this._submit);

    billingConfigModel.loadOnce();
    financialFeatureConfigModel.loadOnce()
      .then(this._renderIfDisplayed);

    this.listenTo(this.getBehavior('appointmentSecureMessages'), 'fetched', this._setEstimateAccountNumberOnPreRegView);
    this.listenTo(this.getBehavior('appointmentType'), 'fetched', this._setAppointmentTypePaymentConfigs);
    this.listenTo(this.getBehavior('appointmentType'), 'change:parent', this._setAppointmentTypePaymentConfigs);
    this.listenTo(this.getBehavior('parentAppointmentType'), 'change:parent', this._setAppointmentTypePaymentConfigs);

    this.listenTo(this.viewState, 'change:allowEstimatedPay', this.render);
    this._setEstimateAccountNumberOnPreRegView();
  },

  /**
   * Start the payment process.  Finish once the payment data is ready.
   * @param [event=undefined] {jQuery.Event} if this method was triggered by a dom event.
   * @private
   */
  _startProcessPayment: function(event) {
    if (event && _.isFunction(event.preventDefault)) {
      event.preventDefault();
    }

    this.unset('serverErrors');
    this.listenTo(this.model, 'paymentDataReady', this._processPayment);
    this.trigger('submit');
  },

  /**
   * @method _processPayment
   * @private
   */
  _processPayment: function() {
    this.stopListening(this.model, 'paymentDataReady', this._processPayment);
    var errors = this.model.validate();
    if (errors) {
      this._scrollToFirstError();
    } else {
      this._submitPaymentAndShowStatus();
    }
  },

  /**
   * Create and show the submit payment modal.
   * This will also hide the select payment method since that will be handled by the modal.
   * @method _submitPaymentAndShowStatus
   * @private
   */
  _submitPaymentAndShowStatus: function () {
    // Hide the select payment method view because the submit modal should now own it.
    // Prevents weird UI things as the submit modal starts to submit the payment.
    // Also hides the cc info immediately for security reasons.
    this.set('hide:select-payment-method', true);
    var submitPaymentModal = this._createNewSubmitPaymentModal();
    this.showModal(submitPaymentModal, this._completePayment, this._paymentFailed);
  },


  /**
   * If we are paying during a prereg get the related account number and set it on the estimates view
   * @method _setEstimateAccountNumberOnPreRegView
   * @private
   */
  _setEstimateAccountNumberOnPreRegView: function () {
    var accountExternalIds = [];
    if (this._accountExternalId) {
      accountExternalIds.push(this._accountExternalId);
    }
    if (!_.isEmpty(this._appointmentExternalIds)) {
      var retrievedAccountExternalIds = this.getBehavior('appointmentSecureMessages').data.get('appointmentDTO.accountExternalId');
      accountExternalIds = accountExternalIds.concat(retrievedAccountExternalIds);
    }
    if (!_.isEmpty(this._appointmentExternalIds) || !_.isEmpty(accountExternalIds)) {
      this._estimatesView.setAccountExternalIds(accountExternalIds);
    }
  },

  /**
   * Creates a new submit payment modal and disposes of any existing submit payment modals.
   *
   * @method _createNewSubmitPaymentModal
   * @return {SubmitPaymentModalView} the newly created submit payment modal.
   * @private
   */
  _createNewSubmitPaymentModal: function() {
    if (this._submitPaymentModal && _.isFunction(this._submitPaymentModal.dispose)) {
      this._submitPaymentModal.dispose();
    }
    this._submitPaymentModal = new SubmitPaymentModalView({
      patientVecnaId: this._patientVecnaId,
      billMessageCollection: this._billMessageCollection,
      insuranceEstimationCollection: this._insuranceEstimationCollection,
      paymentDetailsFormModel: this.model,
      storedPaymentMethodService: this._storedPaymentMethodService,
      selectPaymentMethodView: this._selectPaymentMethodView
    });
    return this._submitPaymentModal;
  },

  /**
   * Hide the submission modal and trigger the parent event to complete the payment
   * @method _completePayment
   * @param response {Object} the response from completing the payment.
   * @param _window {window} the window object to use (dependency injection for jasmine testing).
   * @private
   */
  _completePayment: function(response, _window) {
    var serverErrors = parseServerErrors(response);
    this.set('serverErrors', serverErrors);

    if (_.isEmpty(serverErrors)) {
      this.tableState.trigger('updateEstimatePaid');
      if (this._isInPreReg) {
        this.trigger('markPreRegCompleted');
      } else {
        var url = applicationUtils.currentApplication.fullUrlPath + '#patient/' + this._patientVecnaId + '/receipt';
        _window = _window || window;
        _window.location.assign(url);
      }
    } else {
      this._paymentFailed();
    }
  },

  /**
   * If the widget is active and attached, then re-add the select payment method view to Widget
   * and scrolls to the first displayed error.
   * @method _paymentFailed
   * @private
   */
  _paymentFailed: function() {
    if (this.isActive() && this.isAttached()) {
      // If the view is rendered explicitly and is attached, then re-show the select-payment-method view.
      this.set('hide:select-payment-method', false);
      this._scrollToFirstError();
    }
  },

  /**
   * Scrolls the page up the first error
   * @method _scrollToFirstError
   * @return a promise that resolves when the animation is complete.
   */
  _scrollToFirstError: function() {
    var firstErrorResult = this.$el.find('[data-error-feedback]:visible').first();
    var hasFirstError = firstErrorResult.length > 0;
    if (hasFirstError) {
      return $('html,body').animate({
        scrollTop: firstErrorResult.offset().top
      }, 500).promise();
    } else {
      return $.Deferred().resolve().promise();
    }
  },

  /**
   * Performs the logic for cancelling a payment, in this case triggering the browser's back button.
   * @method _cancelPayment
   * @param event the event that triggered this call.
   * @private
   */
  _cancelPayment: function(event) {
    if (event && _.isFunction(event.preventDefault)) {
      event.preventDefault();
    }
    history.back();
  },

  /**
   * Run method to populate all payment amount fields with total amount due
   * @method _payAll
   * @private
   */
  _payAll: function(){
    this._payAllEstimates();
    var allowOutstandingPay = this._billsView && this._billsView.isAttached() && this._billsView.hasPayableBills() && !this.get('hide:bills');
    if (allowOutstandingPay) {
      this.model.payAllBills();
    }
  },

  /**
   * Set payment amount of each estimate to amount due.
   * @method payAllEstimates
   * @private
   */
  _payAllEstimates: function() {
    var paymentDetailsFormModel = this.model;
    var allowEstimatedPay = this.get('allowEstimatedPay');
    if (allowEstimatedPay) {
      _.each(this._estimatesView.collection.models, function(estimateModel) {
        if (!estimateModel.get('hasUnapprovedInsuranceUpdates')) {
          paymentDetailsFormModel.payFullEstimate(estimateModel);
        }
      });
    }
  },

  /**
   * Run validation on all sub-views.
   * @method _submit
   * @private
   */
  _submit: function() {
    this._billsView.trigger('submit');
    this._selectPaymentMethodView.trigger('submit');
  },

  /**
   * @method _dispose
   * @private
   * @override
   */
  _dispose: function() {
    this._storedPaymentMethodService.dispose();
  },

  /**
   * Sets whether or not to allow outstanding pay or estimated pay based on appointment type configs, if in pre reg wizard
   * @method _setAppointmentTypePaymentConfigs
   * @private
   */
  _setAppointmentTypePaymentConfigs: function() {
    var appointmentTypes = this.getBehavior('appointmentType').data.getModels();
    if (_.isEmpty(appointmentTypes)) {
      return;
    }
    var allowEstimatedPay = _.some(appointmentTypes, function(appointmentType) {
      return appointmentType.getEffectiveAllowEstimatedPay();
    });

    var allowOutstandingPay = _.some(appointmentTypes, function(appointmentType) {
      return appointmentType.getEffectiveAllowOutstandingPay();
    });

    this.set('hide:estimates', !allowEstimatedPay);
    this.set('hide:bills', !allowOutstandingPay);

    var detailsTypes = [];
    if (allowEstimatedPay) {
      detailsTypes.push(PaymentDetailsType.INSURANCE_ESTIMATION, PaymentDetailsType.APPOINTMENT_ESTIMATE);
    }
    if (allowOutstandingPay) {
      detailsTypes.push(PaymentDetailsType.OUTSTANDING_BILL);
    }
    this.model.set('detailsTypes', detailsTypes);
    this._setAllowEstimatedPay();
  },

  /**
   * Get total unpaid balance based on the appointment types configurations
   * @method _getTotalAmountUnpaidDue
   * @private
   * @return total {Number} unpaid balance
   */
  _getTotalAmountUnpaidDue: function() {
    var allowEstimatedPay = this.get('allowEstimatedPay');
    var totalEstimatesUnpaidDue = this.model.getTotalEstimatesUnpaidDue();
    totalEstimatesUnpaidDue = allowEstimatedPay ? totalEstimatesUnpaidDue : 0;

    var allowOutstandingPay = !this.get('hide:bills');
    var totalBillsUnpaidDue = this.model.getTotalBillsUnpaidDue();
    totalBillsUnpaidDue = allowOutstandingPay ? totalBillsUnpaidDue : 0;

    return totalEstimatesUnpaidDue + totalBillsUnpaidDue;
  },

  /**
   * Set allowEstimatedPay to true if there's no pending insurance updates and the estimates are not hidden.
   * @method _setAllowEstimatedPay
   * @private
   */
  _setAllowEstimatedPay: function() {
    var allowEstimatedPay = this._estimatesView && this._estimatesView.isAttached() && this._estimatesView.hasPayableEstimates() && !this.get('hide:estimates');
    this.set('allowEstimatedPay', allowEstimatedPay);
  },

  /**
   * @method postrender
   * @override
   */
  postrender: function() {
    this._setAllowEstimatedPay();
  }
});
